import {
  Box,
  Divider,
  Pagination,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import React, { useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { Schema } from '../../../../amplify/data/resource';
import Select from '../../../components/custom/Select';
import TextField from '../../../components/custom/TextField';
import { UserRole } from '../../../utils/types/types';
import AddClient from './AddClient';
import ClientsList from './ClientsList/ClientsList';

const client = generateClient<Schema>();

const Clients: React.FC = () => {
  const theme = useTheme();

  const [dialogOpen, setDialogOpen] = useState(false);

  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [filterOption, setFilterOption] = useState<string>('שם העסק');
  const [filter, setFilter] = useState<string>('');
  const limit = 10;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const subscription = client.models.User.observeQuery({
        filter: {
          ...(filterOption === 'שם העסק' && {
            companyName: { contains: filter },
          }),
          ...(filterOption === 'שם מורשה חתימה' && {
            authorizedSignatoryName: { contains: filter },
          }),
          ...(filterOption === 'ח.פ.' && {
            companyRegistrationNumber: { contains: filter },
          }),
          ...(filterOption === 'טלפון לשליחת ווטסאפ' && {
            authorizedSignatoryPhone: { contains: filter },
          }),
          ...(filterOption === 'מייל' && { email: { contains: filter } }),
        },
        selectionSet: ['id', 'email', 'createdAt', 'updatedAt', 'role', 'companyName', 'companyRegistrationNumber', 'authorizedSignatoryName', 'authorizedSignatoryPhone', 'paymentPlanId'],
      }).subscribe({
        next: ({ items }: { items: any[] }) => {
          const clientsData = items
            .filter((item) => item.role === UserRole.CLIENT)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

          setClients(clientsData);
          setTotalPages(Math.ceil(clientsData.length / limit));
          setLoading(false);
        },
        error: (error: any) => {
          console.error('Error fetching clients:', error);
          setLoading(false);
        },
      });

      return () => subscription.unsubscribe();
    } catch (error) {
      console.error('Error during fetchUsers:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, filterOption]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleChangeOption = (value: string) => {
    setFilterOption(value);
    setCurrentPage(1);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && (filter || filterOption)) {
        fetchUsers();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [filter, filterOption]);

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ cursor: 'pointer' }}
            onClick={() => setDialogOpen(true)}
          >
            <img
              alt="logo"
              src={'/assets/client/add.svg'}
              style={{ width: '35px', height: '35px' }}
              onClick={() => setDialogOpen(true)}
            />
            <Typography
              fontSize="30px"
              fontWeight="700"
              fontFamily={'AlmoniDL'}
            >
              הוסף לקוח
            </Typography>
          </Stack>
        </Box>
        <Typography
          fontSize="30px"
          fontWeight="700"
          fontFamily={'AlmoniDL'}
          textAlign={'right'}
          sx={{ whiteSpace: 'nowrap' }}
        >
          לקוחות
        </Typography>
      </Box>
      <Divider
        sx={{
          width: '100%',
          height: '1px',
          backgroundColor: theme.palette.dark.border,
        }}
      />
      {loading ? (
        <Box sx={{ mt: '100px', display: 'flex', justifyContent: 'center' }}>
          <ReactLoading
            type={'bubbles'}
            color={theme.palette.green.main}
            height={250}
            width={250}
          />
        </Box>
      ) : clients.length > 0 ? (
        <>
          <Stack
            direction="row-reverse"
            justifyContent="flex-start"
            alignItems="center"
            spacing={5}
          >
            <Stack direction="row-reverse" spacing={5}>
              <Select
                id="filter-select"
                label="בחר שדה"
                onChange={(value: string) => handleChangeOption(value)}
                value={filterOption}
                sx={{ width: '250px', ml: 1 }}
                options={[
                  { value: 'שם העסק', label: 'שם העסק' },
                  { value: 'שם מורשה חתימה', label: 'שם מורשה חתימה' },
                  { value: 'ח.פ.', label: 'ח.פ.' },
                  {
                    value: 'טלפון לשליחת ווטסאפ',
                    label: 'טלפון לשליחת ווטסאפ',
                  },
                  { value: 'מייל', label: 'מייל' },
                ]}
              />

              <TextField
                contrast
                value={filter}
                onChange={handleChange}
                label="מסנן"
                sx={{ width: '300px' }}
              />
            </Stack>
          </Stack>
          <ClientsList
            clients={clients.slice(
              (currentPage - 1) * limit,
              currentPage * limit,
            )}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              margin: '20px 0',
            }}
          >
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                shape="rounded"
              />
            </Stack>
          </Box>
        </>
      ) : (
        <>
          <Stack
            direction="row-reverse"
            justifyContent="flex-start"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="row-reverse" spacing={5}>
              <Select
                id="filter-select"
                label="בחר שדה"
                onChange={(value: string) => handleChangeOption(value)}
                value={filterOption}
                options={[
                  { value: 'שם העסק', label: 'שם העסק' },
                  { value: 'שם מורשה חתימה', label: 'שם מורשה חתימה' },
                  { value: 'ח.פ.', label: 'ח.פ.' },
                  {
                    value: 'טלפון לשליחת ווטסאפ',
                    label: 'טלפון לשליחת ווטסאפ',
                  },
                  { value: 'מייל', label: 'מייל' },
                ]}
              />

              <TextField
                contrast
                value={filter}
                onChange={handleChange}
                label="מסנן"
                sx={{ width: '300px', mr: -4 }}
              />
            </Stack>
          </Stack>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              marginTop: '20px',
              fontFamily: 'AlmoniDL',
            }}
          >
            אין משתמשים
          </Typography>
        </>
      )}
      <AddClient
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default Clients;
